import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { getHcpProfileMe } from './hcpProfileApi';

function normalizeGeneralSegment(gen) {
  if (!gen || typeof gen !== 'object') return gen;
  const out = { ...gen };
  const pairs = [
    ['firstName', 'first_name'],
    ['lastName', 'last_name'],
    ['dateOfBirth', 'date_of_birth'],
    ['idNumber', 'id_number'],
    ['countryCode', 'country_code'],
    ['phoneNumber', 'phone_number'],
    ['addressCountry', 'address_country'],
    ['addressState', 'address_state'],
    ['addressCity', 'address_city'],
    ['addressStreet', 'address_street'],
    ['addressZipCode', 'address_zip_code'],
  ];
  for (const [camel, snake] of pairs) {
    const empty =
      out[camel] === undefined ||
      out[camel] === null ||
      (typeof out[camel] === 'string' && out[camel] === '');
    if (!empty) continue;
    if (out[snake] !== undefined && out[snake] !== null && out[snake] !== '') {
      out[camel] = out[snake];
    }
  }
  if (
    (out.categoryName === undefined ||
      out.categoryName === null ||
      out.categoryName === '') &&
    out.category_name != null &&
    out.category_name !== ''
  ) {
    out.categoryName = out.category_name;
  }
  if (out.categoryId == null && out.category_id != null) {
    out.categoryId = out.category_id;
  }
  if (!out.idImage && out.id_image) out.idImage = out.id_image;
  if (out.dateOfBirth instanceof Date) {
    out.dateOfBirth = out.dateOfBirth.toISOString().slice(0, 10);
  } else if (
    typeof out.dateOfBirth === 'string' &&
    out.dateOfBirth.length > 10
  ) {
    out.dateOfBirth = out.dateOfBirth.slice(0, 10);
  }
  return out;
}

/** Follow accidental `{ data: ... }` wrapping (up to a few levels). */
function peelProfileBundle(obj) {
  let cur = obj && typeof obj === 'object' ? { ...obj } : obj;
  for (let i = 0; i < 4; i += 1) {
    if (!cur || typeof cur !== 'object') break;
    const hasBundleShape =
      cur.general != null ||
      Array.isArray(cur.licenses) ||
      cur.publicHcpId != null ||
      cur.public_hcp_id != null ||
      cur.status != null;
    if (hasBundleShape) return cur;
    if (cur.data && typeof cur.data === 'object') cur = cur.data;
    else break;
  }
  return cur;
}

/** @param {unknown} raw */
export function sanitizeProfileBundle(raw) {
  const n = normalizeProfileMeData(raw);
  if (!n || typeof n !== 'object') return null;
  const g =
    n.general && typeof n.general === 'object'
      ? normalizeGeneralSegment(n.general)
      : {};
  return {
    ...n,
    general: g,
    licenses: Array.isArray(n.licenses) ? n.licenses : [],
    educations: Array.isArray(n.educations) ? n.educations : [],
    experience: n.experience ?? null,
    lockedGeneralPaths: Array.isArray(n.lockedGeneralPaths)
      ? n.lockedGeneralPaths
      : [],
    generalLockMessages:
      n.generalLockMessages && typeof n.generalLockMessages === 'object'
        ? n.generalLockMessages
        : {},
    profileFieldPermissions:
      n.profileFieldPermissions && typeof n.profileFieldPermissions === 'object'
        ? n.profileFieldPermissions
        : {},
    validationConfig:
      n.validationConfig && typeof n.validationConfig === 'object'
        ? n.validationConfig
        : {},
    licenseFieldConfig:
      n.licenseFieldConfig && typeof n.licenseFieldConfig === 'object'
        ? n.licenseFieldConfig
        : {},
    licenseRepeatableLimits:
      n.licenseRepeatableLimits && typeof n.licenseRepeatableLimits === 'object'
        ? n.licenseRepeatableLimits
        : {},
    educationRepeatableLimits:
      n.educationRepeatableLimits &&
      typeof n.educationRepeatableLimits === 'object'
        ? n.educationRepeatableLimits
        : {},
    attachmentRevisions: Array.isArray(n.attachmentRevisions)
      ? n.attachmentRevisions
      : [],
  };
}

/** Request/review views: current file only — no superseded attachment audit list. */
export function profileBundleWithoutAttachmentRevisions(bundle) {
  if (!bundle || typeof bundle !== 'object') return bundle;
  return { ...bundle, attachmentRevisions: [] };
}

/** Normalize `/me` bundle: unwrap mistaken nesting, camelCase `publicHcpId`, general segment keys. */
function normalizeProfileMeData(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  let next = peelProfileBundle(raw);

  const id = next.publicHcpId ?? next.public_hcp_id ?? null;
  if (id != null && next.publicHcpId == null) {
    next = { ...next, publicHcpId: id };
  }

  if (next.general && typeof next.general === 'object') {
    next = { ...next, general: normalizeGeneralSegment(next.general) };
  }
  return next;
}

/**
 * Profile data lifecycle:
 *
 * - **Initial load**: `loading` true until GET `/me` succeeds (cache-busted).
 * - **After any save**: Children call {@link refreshProfile} (often as `onSaved`) so the SPA
 *   immediately reflects server state; failures toast but keep the last known `profileData`.
 * - **Full page refresh**: Runs the same GET with `Cache-Control: no-cache` + a `_` query param
 *   so intermediary caches do not serve stale snapshots.
 */

export function useHcpProfileModule(options = {}) {
  const enabled = options.enabled !== false;
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [errorMessage, setErrorMessage] = useState(null);
  const profileDataRef = useRef(null);
  useEffect(() => {
    profileDataRef.current = profileData;
  }, [profileData]);

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  /**
   * Refetch authoritative profile bundle. Call after mutations.
   */
  const refreshProfile = useCallback(async () => {
    if (!enabledRef.current) {
      return;
    }
    const hadData = Boolean(profileDataRef.current);
    setLoading(true);
    if (!hadData) {
      setErrorMessage(null);
    }
    try {
      const res = await getHcpProfileMe();
      if (res.success && res.data) {
        const safe = sanitizeProfileBundle(res.data);
        if (safe) {
          setProfileData(safe);
        } else {
          const msg = 'Could not load profile';
          if (hadData) {
            toast.error(msg);
          } else {
            setErrorMessage(msg);
          }
        }
      } else {
        const msg = res.message || 'Could not load profile';
        if (hadData) {
          toast.error(msg);
        } else {
          setErrorMessage(msg);
        }
      }
    } catch (e) {
      const showToast =
        e.response?.status !== 404 ||
        !String(e.config?.url || '').includes('hcp/profile');
      const msg =
        e.response?.data?.message || e.message || 'Could not load profile';
      if (hadData) {
        toast.error(msg);
      } else {
        setErrorMessage(msg);
        if (showToast) {
          toast.error(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setErrorMessage(null);
      return;
    }
    refreshProfile();
  }, [enabled, refreshProfile]);

  return {
    profileData,
    loading,
    errorMessage,
    refreshProfile,
    /** @deprecated Alias for refreshProfile */
    reload: refreshProfile,
  };
}
